import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SafetyShell from "../safety/SafetyShell";
import { BarChart3, Download, RefreshCw, Filter, XCircle, ArrowUp, ArrowDown, ChevronsUpDown, TrendingUp, TrendingDown, Minus, LineChart as LineChartIcon } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LineChart, Line, Legend } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const fmtUSD = (n) => (n == null ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }));
const fmtINR = (n) => (n == null ? "—" : n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }));
const fmtNum = (n) => (n == null ? "—" : n.toLocaleString("en-US"));

const BAR_COLORS = ["#4f46e5", "#6366f1", "#7c3aed", "#8b5cf6", "#a855f7", "#0ea5e9", "#0891b2", "#0d9488", "#059669", "#65a30d"];

function ProductDemandReport({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rankBy, setRankBy] = useState("qty"); // qty | value
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [verticalId, setVerticalId] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" }); // client-side table sort override
  const [trend, setTrend] = useState(null);
  const [trendTop, setTrendTop] = useState(5); // 5 | 10
  const [trendLoading, setTrendLoading] = useState(true);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    p.set("rank_by", rankBy);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (customerId) p.set("customer_id", customerId);
    if (verticalId) p.set("vertical_id", verticalId);
    return p.toString();
  }, [rankBy, dateFrom, dateTo, customerId, verticalId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/management/product-demand?${buildParams()}`, authHeaders());
      setData(res.data);
      setSort({ key: null, dir: "asc" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load product demand report");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const fetchTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const res = await axios.get(`${API}/management/product-demand/trend?${buildParams()}&top=${trendTop}`, authHeaders());
      setTrend(res.data);
    } catch {
      setTrend(null);
    } finally {
      setTrendLoading(false);
    }
  }, [buildParams, trendTop]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/management/product-demand/export.xlsx?${buildParams()}`, { ...authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `product_demand_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const clearFilters = () => { setDateFrom(""); setDateTo(""); setCustomerId(""); setVerticalId(""); setSearch(""); };
  const hasFilters = dateFrom || dateTo || customerId || verticalId;

  const items = data?.items || [];

  const filteredItems = useMemo(() => {
    let rows = items;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      rows = rows.filter((r) => (r.product_code || "").includes(q) || (r.description || "").toUpperCase().includes(q));
    }
    if (sort.key) {
      const dir = sort.dir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        if (typeof va === "string") return (va || "").localeCompare(vb || "") * dir;
        return ((va ?? 0) - (vb ?? 0)) * dir;
      });
    }
    return rows;
  }, [items, search, sort]);

  const top10 = useMemo(() => items.slice(0, 10).map((r) => ({
    code: r.product_code,
    value: rankBy === "value" ? r.total_value_usd : rankBy === "pallets" ? (r.total_pallets || 0) : r.total_qty,
  })), [items, rankBy]);

  const trendChartData = useMemo(() => {
    if (!trend?.months?.length) return [];
    const key = rankBy === "value" ? "by_month_val" : "by_month_qty";
    return trend.months.map((m) => {
      const row = { month: m };
      trend.series.forEach((s) => { row[s.product_code] = s[key][m] ?? 0; });
      return row;
    });
  }, [trend, rankBy]);

  const trendMeta = { rising: { icon: TrendingUp, cls: "text-emerald-600 bg-emerald-50" }, fading: { icon: TrendingDown, cls: "text-rose-600 bg-rose-50" }, steady: { icon: Minus, cls: "text-zinc-500 bg-zinc-100" } };

  const toggleSort = (key) => {
    setSort((s) => (s.key !== key ? { key, dir: "desc" } : s.dir === "desc" ? { key, dir: "asc" } : { key: null, dir: "asc" }));
  };

  const SortHead = ({ label, k, align = "right" }) => (
    <th className={`px-3 py-2 text-${align} whitespace-nowrap`}>
      <button className="inline-flex items-center gap-1 hover:text-indigo-600" onClick={() => toggleSort(k)} data-testid={`pd-sort-${k}`}>
        {label}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
      </button>
    </th>
  );

  const t = data?.totals || {};

  return (
    <SafetyShell
      user={user}
      onLogout={onLogout}
      breadcrumb={[{ label: "Safety", to: "/safety" }, { label: "Product Demand" }]}
      miniMap={[
        { label: "Safety", to: "/safety", depth: 0 },
        { label: "Purchase Orders", to: "/safety/po", depth: 1 },
        { label: "Management Report", to: "/safety/management-report", depth: 1 },
        { label: "Product Demand", active: true, depth: 1 },
      ]}
    >
      <Toaster richColors position="top-right" />
      <div data-testid="product-demand-page">
        {/* Header */}
        <div className="sf-surface p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-xl font-bold tracking-tight">Product Demand Report</h1>
              <p className="text-xs text-zinc-500">
                Every product ranked <b>most → least ordered</b> across all purchase orders. Quantity is the ordered qty on each PO line;
                value is Σ(qty × unit price). ₹ uses the latest shipping-bill FX rate.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading} data-testid="pd-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={exportExcel} disabled={!items.length} data-testid="pd-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export Excel
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="sf-surface p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[11px] font-medium text-zinc-500 mb-1">Rank by</label>
              <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="pd-rankby">
                <button
                  className={`px-3 py-1.5 text-sm ${rankBy === "qty" ? "bg-indigo-600 text-white" : "bg-white text-zinc-700"}`}
                  onClick={() => setRankBy("qty")} data-testid="pd-rankby-qty"
                >Quantity</button>
                <button
                  className={`px-3 py-1.5 text-sm border-l border-zinc-200 ${rankBy === "pallets" ? "bg-indigo-600 text-white" : "bg-white text-zinc-700"}`}
                  onClick={() => setRankBy("pallets")} data-testid="pd-rankby-pallets"
                >Pallets</button>
                <button
                  className={`px-3 py-1.5 text-sm border-l border-zinc-200 ${rankBy === "value" ? "bg-indigo-600 text-white" : "bg-white text-zinc-700"}`}
                  onClick={() => setRankBy("value")} data-testid="pd-rankby-value"
                >Order Value ($)</button>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-500 mb-1">PO date from</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="pd-date-from" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-500 mb-1">PO date to</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="pd-date-to" />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-500 mb-1">Customer</label>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="h-9 w-52 rounded-md border border-zinc-200 px-2 text-sm bg-white" data-testid="pd-customer">
                <option value="">All customers</option>
                {(data?.customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-zinc-500 mb-1">Vertical</label>
              <select value={verticalId} onChange={(e) => setVerticalId(e.target.value)} className="h-9 w-48 rounded-md border border-zinc-200 px-2 text-sm bg-white" data-testid="pd-vertical">
                <option value="">All verticals</option>
                {(data?.verticals || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                <option value="__unassigned__">Unassigned</option>
              </select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="pd-clear-filters">
                <XCircle className="w-4 h-4 mr-1.5" /> Clear
              </Button>
            )}
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="pd-summary">
          <div className="sf-surface p-3">
            <div className="text-[11px] text-zinc-500">Distinct products</div>
            <div className="text-lg font-bold">{fmtNum(t.product_count)}</div>
          </div>
          <div className="sf-surface p-3">
            <div className="text-[11px] text-zinc-500">Total qty ordered</div>
            <div className="text-lg font-bold">{fmtNum(t.total_qty)}</div>
          </div>
          <div className="sf-surface p-3">
            <div className="text-[11px] text-zinc-500">Total pallets</div>
            <div className="text-lg font-bold" data-testid="pd-total-pallets">{fmtNum(t.total_pallets)}</div>
          </div>
          <div className="sf-surface p-3">
            <div className="text-[11px] text-zinc-500">Total order value</div>
            <div className="text-lg font-bold">{fmtUSD(t.total_value_usd)}</div>
            <div className="text-[11px] text-zinc-500">{fmtINR(t.total_value_inr)}</div>
          </div>
          <div className="sf-surface p-3">
            <div className="text-[11px] text-zinc-500">POs counted</div>
            <div className="text-lg font-bold">{fmtNum(t.po_count)}</div>
          </div>
        </div>

        {/* Top-10 chart */}
        {top10.length > 0 && (
          <div className="sf-surface p-4 mb-4" data-testid="pd-chart">
            <div className="text-sm font-semibold mb-2">Top 10 by {rankBy === "value" ? "order value ($)" : rankBy === "pallets" ? "pallets" : "quantity"}</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={top10} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="code" angle={-35} textAnchor="end" height={60} tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (rankBy === "value" ? `$${(v / 1000).toFixed(0)}k` : v.toLocaleString())} />
                <Tooltip formatter={(v) => (rankBy === "value" ? fmtUSD(v) : fmtNum(v))} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {top10.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Demand Trend — month-by-month */}
        <div className="sf-surface p-4 mb-4" data-testid="pd-trend">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="text-sm font-semibold flex items-center gap-2">
              <LineChartIcon className="w-4 h-4 text-indigo-600" /> Demand trend — {rankBy === "value" ? "order value ($)" : "quantity"} by month
            </div>
            <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="pd-trend-top">
              {[5, 10].map((n) => (
                <button key={n} className={`px-3 py-1 text-xs ${trendTop === n ? "bg-indigo-600 text-white" : "bg-white text-zinc-700"}`}
                  onClick={() => setTrendTop(n)} data-testid={`pd-trend-top-${n}`}>Top {n}</button>
              ))}
            </div>
          </div>
          {trendLoading ? (
            <div className="py-10 text-center text-zinc-400 text-sm" data-testid="pd-trend-loading">Loading trend…</div>
          ) : !trendChartData.length ? (
            <div className="py-10 text-center text-zinc-400 text-sm" data-testid="pd-trend-empty">No dated POs to plot a trend.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (rankBy === "value" ? `$${(v / 1000).toFixed(0)}k` : v.toLocaleString())} />
                  <Tooltip formatter={(v) => (rankBy === "value" ? fmtUSD(v) : fmtNum(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(trend?.series || []).map((s, i) => (
                    <Line key={s.product_code} type="monotone" dataKey={s.product_code} stroke={BAR_COLORS[i % BAR_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="pd-trend-badges">
                {(trend?.series || []).map((s) => {
                  const meta = trendMeta[s.trend] || trendMeta.steady;
                  const Icon = meta.icon;
                  return (
                    <span key={s.product_code} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${meta.cls}`} data-testid={`pd-trend-badge-${s.product_code}`}>
                      <Icon className="w-3 h-3" /> {s.product_code} · {s.trend}
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Table */}
        <div className="sf-surface p-0 overflow-hidden">
          <div className="p-3 flex items-center justify-between gap-2 flex-wrap border-b border-zinc-100">
            <div className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-600" /> Ranked products
              {data?.fx_rate && <span className="text-[11px] font-normal text-zinc-400">· FX ₹{data.fx_rate}/$ ({data.fx_source})</span>}
            </div>
            <div className="relative">
              <Filter className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code / description" className="h-8 w-64 pl-7 text-sm" data-testid="pd-search" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pd-table">
              <thead className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <SortHead label="Qty ordered" k="total_qty" />
                  <SortHead label="Pallets" k="total_pallets" />
                  <SortHead label="# POs" k="po_count" />
                  <SortHead label="# Cust." k="customer_count" />
                  <SortHead label="Value ($)" k="total_value_usd" />
                  <th className="px-3 py-2 text-right whitespace-nowrap">Value (₹)</th>
                  <SortHead label="Share %" k="share_pct" />
                  <SortHead label="Cumul. %" k="cumulative_pct" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="px-3 py-10 text-center text-zinc-400" data-testid="pd-loading">Loading…</td></tr>
                ) : filteredItems.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-10 text-center text-zinc-400" data-testid="pd-empty">No products match the current filters.</td></tr>
                ) : filteredItems.map((r) => (
                  <tr key={r.product_code} className="border-t border-zinc-100 hover:bg-indigo-50/40" data-testid={`pd-row-${r.product_code}`}>
                    <td className="px-3 py-2 text-zinc-400 font-medium">{r.rank}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-zinc-800">{r.product_code}</div>
                      <div className="text-[11px] text-zinc-400 truncate max-w-[280px]">{r.description}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtNum(r.total_qty)}</td>
                    <td className="px-3 py-2 text-right text-zinc-700 tabular-nums" data-testid={`pd-pallets-${r.product_code}`}>{r.total_pallets != null ? fmtNum(r.total_pallets) : "—"}</td>
                    <td className="px-3 py-2 text-right text-zinc-600">{r.po_count}</td>
                    <td className="px-3 py-2 text-right text-zinc-600">{r.customer_count}</td>
                    <td className="px-3 py-2 text-right text-zinc-700">{fmtUSD(r.total_value_usd)}</td>
                    <td className="px-3 py-2 text-right text-zinc-500">{fmtINR(r.total_value_inr)}</td>
                    <td className="px-3 py-2 text-right text-zinc-600">{r.share_pct}%</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden hidden sm:block">
                          <div className="h-full bg-indigo-500" style={{ width: `${r.cumulative_pct}%` }} />
                        </div>
                        <span className="text-zinc-500 tabular-nums">{r.cumulative_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SafetyShell>
  );
}

export default ProductDemandReport;
